import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./ShareTheLoadLanding.module.css";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const navigationLinks = [
  { id: "features", label: "Features" },
  { id: "how-it-works", label: "How It Works" },
  { id: "faq", label: "FAQ" },
];

const features = [
  {
    icon: "✅",
    title: "Delegate Chores",
    description: "Assign household tasks to family members and track who's done what — no more nagging.",
  },
  {
    icon: "📅",
    title: "Shared Family Calendar",
    description: "Keep everyone on the same schedule. Scan event flyers with your camera and they're added instantly.",
  },
  {
    icon: "🛒",
    title: "Collaborative Shopping List",
    description: "One list, every device. Add items from anywhere and check them off in real time.",
  },
  {
    icon: "🍽️",
    title: "Meal Planning",
    description: "Plan the week's dinners together so nobody has to answer 'what's for dinner?' ever again.",
  },
  {
    icon: "📣",
    title: "Family Announcements",
    description: "Post reminders and updates for the whole family to see — no group chat chaos.",
  },
  {
    icon: "⚡",
    title: "Real-Time Sync",
    description: "Every change is live for every family member the moment it's made. No refreshing, no lag.",
  },
];

const steps = [
  {
    number: "01",
    title: "Create your family",
    description: "Set up your family group in seconds. You're the parent — you're in charge.",
  },
  {
    number: "02",
    title: "Invite by email",
    description: "Send secure email invites to your partner, kids, or anyone in the household.",
  },
  {
    number: "03",
    title: "Share the load",
    description: "Delegate tasks, add events, plan meals. Everyone sees everything in real time.",
  },
];

const faqs = [
  {
    question: "Who is Share the Load for?",
    answer: "Families who want to stop relying on sticky notes and group texts to stay coordinated. Works great for parents, blended families, and any household with more than one person.",
  },
  {
    question: "Is it free?",
    answer: "Share the Load will launch with a free tier that covers everything a family needs. A paid plan will unlock higher limits and premium features like kid mode with gamified chores.",
  },
  {
    question: "How does event scanning work?",
    answer: "Tap 'Scan Flyer' in the calendar tab, point your camera at any event flyer or permission slip, and the app reads the date, title, and location automatically. Review and submit — done.",
  },
  {
    question: "When does it launch?",
    answer: "We're putting the finishing touches on the iOS app now. Join the waitlist and you'll be first to know.",
  },
];

export const ShareTheLoadLanding: React.FC = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={styles.page}>
      <a href="#main-content" className={styles.skipLink}>Skip to main content</a>

      <nav className={`${styles.nav} ${isScrolled ? styles.navScrolled : ""}`}>
        <div className={styles.navContainer}>
          <Link to="/share-the-load" className={styles.logo}>
            <LogoIcon />
            <span>Share the Load</span>
          </Link>

          <ul className={styles.navLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button onClick={() => scrollToSection(link.id)} className={styles.navLink}>
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          <Link to="/share-the-load/waitlist" className={styles.navCta}>
            Join Waitlist
          </Link>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`} />
          </button>
        </div>

        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ""}`}>
          <ul className={styles.mobileNavLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button onClick={() => scrollToSection(link.id)} className={styles.mobileNavLink}>
                  {link.label}
                </button>
              </li>
            ))}
            <li>
              <Link to="/share-the-load/waitlist" className={styles.mobileCta} onClick={() => setIsMobileMenuOpen(false)}>
                Join the Waitlist
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      <main id="main-content" role="main" tabIndex={-1}>
        {/* Hero */}
        <section className={styles.hero}>
          <div className={styles.heroGlow} />
          <div className={styles.heroContainer}>
            <div className={styles.badge}>Coming Soon to iOS</div>
            <h1 className={styles.heroHeadline}>
              Run your household<br />
              <span className={styles.heroAccent}>without running yourself ragged.</span>
            </h1>
            <p className={styles.heroSubheadline}>
              Share the Load is the family app that actually gets used — chores, events, shopping, and meal planning in one place your whole family can see in real time.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/share-the-load/waitlist" className={styles.primaryCta}>
                Join the Waitlist
              </Link>
              <button onClick={() => scrollToSection("how-it-works")} className={styles.secondaryCta}>
                See how it works
              </button>
            </div>
            <p className={styles.heroNote}>Free to join. One email when we launch. No spam.</p>
          </div>
        </section>

        {/* Features */}
        <section id="features" className={styles.features}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Everything your family needs</h2>
              <p className={styles.sectionSubtitle}>
                Six tools in one app. No switching between tabs, texts, and sticky notes.
              </p>
            </div>
            <div className={styles.featuresGrid}>
              {features.map((f) => (
                <div key={f.title} className={styles.featureCard}>
                  <div className={styles.featureEmoji}>{f.icon}</div>
                  <h3 className={styles.featureTitle}>{f.title}</h3>
                  <p className={styles.featureDescription}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className={styles.howItWorks}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Up and running in minutes</h2>
              <p className={styles.sectionSubtitle}>No tutorials. No setup calls. Three steps.</p>
            </div>
            <div className={styles.stepsGrid}>
              {steps.map((step) => (
                <div key={step.number} className={styles.stepCard}>
                  <div className={styles.stepNumber}>{step.number}</div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className={styles.faq}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Common questions</h2>
            </div>
            <div className={styles.faqList}>
              {faqs.map((item, i) => (
                <div key={i} className={`${styles.faqItem} ${openFaq === i ? styles.faqItemOpen : ""}`}>
                  <button
                    className={styles.faqQuestion}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                  >
                    <span>{item.question}</span>
                    <span className={styles.faqChevron}>{openFaq === i ? "−" : "+"}</span>
                  </button>
                  {openFaq === i && (
                    <p className={styles.faqAnswer}>{item.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={styles.cta}>
          <div className={styles.ctaContainer}>
            <h2 className={styles.ctaTitle}>Ready to share the load?</h2>
            <p className={styles.ctaSubtitle}>
              Join the waitlist and be first in line when Share the Load launches on iOS.
            </p>
            <Link to="/share-the-load/waitlist" className={styles.primaryCta}>
              Join the Waitlist
            </Link>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerBrand}>
            <Link to="/share-the-load" className={styles.footerLogo}>
              <LogoIcon />
              <span>Share the Load</span>
            </Link>
            <p className={styles.footerTagline}>Family coordination, finally solved.</p>
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h3>Product</h3>
              <ul>
                <li><button onClick={() => scrollToSection("features")}>Features</button></li>
                <li><button onClick={() => scrollToSection("how-it-works")}>How It Works</button></li>
                <li><button onClick={() => scrollToSection("faq")}>FAQ</button></li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3>Company</h3>
              <ul>
                <li><Link to="/">Dixon App Development</Link></li>
                <li><a href="mailto:dixonappdevelopment@gmail.com">Contact</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Dixon App Development. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
